import { FC } from 'react';
import cx from 'classnames';

import { Icon, Typography } from 'src/components-v2/DataDisplay';
import { Link } from 'src/components/Inputs';

import { Card } from '../Card';
import { List } from '../List';
import {
  ColumnItem,
  CardWIcon,
  CardWImage,
  ImageColumnItem,
  CustomerStoriesColumnItem,
  isCardWIcon,
  isCardWImage,
  isImageColumnItem,
  isCustomerStoriesColumnItem,
} from '../types';

import styles from './Columns.module.scss';

// Customer story logo images
const customerStoryImages: Record<
  string,
  { src: string; width: number; height: number; alt: string }
> = {
  affirm: {
    src: 'https://images.ctfassets.net/ss5kfr270og3/74lu4wEJLRNTiZgtv8kf0N/c8ed6399cdaa38be54d78e31a9adb570/affirm-logo.png',
    width: 38.68,
    height: 15.43,
    alt: '',
  },
  robinhood: {
    src: 'https://images.ctfassets.net/ss5kfr270og3/4syXDPD9rqQQbMfXB75rBo/5a0ff8d09baaba115eb90783bbf43bac/robinhood-logo.png',
    width: 59.2,
    height: 11.61,
    alt: '',
  },
  chime: {
    src: 'https://images.ctfassets.net/ss5kfr270og3/1nz7suAOHxLcuGrXWIpTin/690fc3b32d026dfb94941705815d77c0/chime-logo.png',
    width: 39,
    height: 11.85,
    alt: '',
  },
};

type ColumnsProps = {
  className?: string;
  items:
    | [ColumnItem, ColumnItem]
    | [
        ColumnItem,
        ColumnItem,
        ColumnItem | ImageColumnItem | CustomerStoriesColumnItem,
      ];
};

const ColumnContent: FC<{ column: ColumnItem }> = ({ column }) => {
  if (!column.children || column.children.length === 0) {
    return null;
  }

  // Check if first item has icon to determine if it's CardWIcon[]
  const hasIcons = column.children.some((item) => {
    return isCardWIcon(item);
  });

  if (hasIcons) {
    const iconItems = column.children.filter(isCardWIcon) as CardWIcon[];
    return <List className={styles.Column__list} items={iconItems} />;
  }

  // Check if first item has image to determine if it's CardWImage[]
  const hasImages = column.children.some((item) => {
    return isCardWImage(item);
  });

  if (hasImages) {
    const imageItems = column.children.filter(isCardWImage) as CardWImage[];
    return (
      <div className={styles.Column__images}>
        {imageItems.map((item, i) => {
          return <Card key={`column-image-${i}`} {...item} tabIndex={-1} />;
        })}
      </div>
    );
  }

  // Render as plain links
  return (
    <ul className={styles.List}>
      {column.children.map((item, i) => {
        const { href, title, locale } = item;

        return (
          <li className={styles.List__item} key={`main-menu-column-item-${i}`}>
            <Link href={href} locale={locale}>
              <a className={styles.Link} role='link' tabIndex={-1}>
                <Typography
                  theme='main-menu-new'
                  variant='p'
                  component='span'
                  sx={{
                    mb: 0,
                  }}
                >
                  {title}
                </Typography>
              </a>
            </Link>
          </li>
        );
      })}
    </ul>
  );
};

const SeeAllLink: FC<{ href: string; title: string }> = ({ href, title }) => {
  return (
    <Link href={href}>
      <a className={styles.SeeAllLink} role='link' tabIndex={-1}>
        {title}
        <span className={styles.SeeAllLink__arrows}>
          <span className={styles.ctaArrowsInner}>
            <Icon icon='ArrowRight' size={14} />
            <Icon icon='ArrowRight' size={14} />
          </span>
        </span>
      </a>
    </Link>
  );
};

export const Columns: FC<ColumnsProps> = ({ className, items }) => {
  const [firstColumn, secondColumn, thirdColumn] = items;

  const isThirdColumnImage = thirdColumn && isImageColumnItem(thirdColumn);
  const isThirdColumnStories =
    thirdColumn && isCustomerStoriesColumnItem(thirdColumn);
  return (
    <div className={cx(styles.Columns, className)}>
      {firstColumn.children && firstColumn.children.length > 0 && (
        <div className={styles.Column}>
          {firstColumn.title && (
            <Typography
              className={styles.Title}
              theme='main-menu-new'
              variant='h6'
            >
              {firstColumn.title}
            </Typography>
          )}
          <ColumnContent column={firstColumn} />
        </div>
      )}

      {secondColumn.children && secondColumn.children.length > 0 && (
        <div className={styles.Column}>
          {secondColumn.title && (
            <Typography
              className={styles.Title}
              theme='main-menu-new'
              variant='h6'
            >
              {secondColumn.title}
            </Typography>
          )}
          <ColumnContent column={secondColumn} />
        </div>
      )}

      {thirdColumn && isThirdColumnImage ? (
        <div className={cx(styles.Column, styles.Column_image)}>
          {thirdColumn.title && (
            <Typography
              className={styles.Title}
              theme='main-menu-new'
              variant='h6'
            >
              {thirdColumn.title}
            </Typography>
          )}
          <Card {...thirdColumn.image} tabIndex={-1} />
        </div>
      ) : thirdColumn && isThirdColumnStories ? (
        <div className={cx(styles.Column, styles.Column_stories)}>
          {thirdColumn.title && (
            <Typography
              className={styles.Title}
              theme='main-menu-new'
              variant='h6'
            >
              {thirdColumn.title}
            </Typography>
          )}
          <ul className={styles.StoriesList}>
            {thirdColumn.stories.map((story, i) => {
              const imageData = customerStoryImages[story.image];

              return (
                <li key={`customer-story-${i}`} className={styles.StoryItem}>
                  <Link href={story.href}>
                    <a className={styles.StoryLink} role='link' tabIndex={-1}>
                      <span className={styles.StoryImageWrapper}>
                        <img
                          src={imageData.src}
                          width={imageData.width}
                          height={imageData.height}
                          alt={imageData.alt}
                        />
                      </span>
                      <Typography
                        theme='main-menu-new'
                        variant='p'
                        component='span'
                        sx={{ mb: 0 }}
                      >
                        {story.title}
                      </Typography>
                    </a>
                  </Link>
                </li>
              );
            })}
          </ul>
          <SeeAllLink
            href={thirdColumn.seeAllLink.href}
            title={thirdColumn.seeAllLink.title}
          />
        </div>
      ) : (
        thirdColumn &&
        !isImageColumnItem(thirdColumn) &&
        !isCustomerStoriesColumnItem(thirdColumn) &&
        thirdColumn.children &&
        thirdColumn.children.length > 0 && (
          <div className={styles.Column}>
            {thirdColumn.title && (
              <Typography
                className={styles.Title}
                theme='main-menu-new'
                variant='h6'
              >
                {thirdColumn.title}
              </Typography>
            )}
            <ColumnContent column={thirdColumn} />
          </div>
        )
      )}
    </div>
  );
};
